/**
 * MDI Cases Section Component
 * 
 * Displays MDI telehealth cases for a specific patient.
 * Shows case status, linked encounters, and prescription information.
 */

import React, { useState, useEffect } from 'react'
import {
  Stethoscope,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Eye,
  FileText,
  Pill,
  ChevronRight,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { mdiService, MdiCase } from '@/services/mdiService'
import { useToast } from '@/hooks/use-toast'
import { useClinic } from '@/contexts/ClinicContext'
import { formatDistanceToNow, format } from 'date-fns'

interface MdiCasesSectionProps {
  patientId: string
  onViewEncounter?: (encounterId: string) => void
  onViewPrescription?: (prescriptionId: string) => void
}

const MdiCasesSection: React.FC<MdiCasesSectionProps> = ({
  patientId,
  onViewEncounter,
  onViewPrescription,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [cases, setCases] = useState<MdiCase[]>([])
  const [mdiEnabled, setMdiEnabled] = useState(false)
  const { toast } = useToast()
  const { clinic } = useClinic()

  const loadCases = async () => {
    if (!clinic?.id) {
      setIsLoading(false)
      return
    }
    
    setIsLoading(true)
    try {
      // Check if MDI is enabled for this clinic
      const enabled = await mdiService.isEnabled(clinic.id)
      setMdiEnabled(enabled)

      if (enabled) {
        const result = await mdiService.listCases({ patientId, clinicId: clinic.id })
        if (result.success && result.data) {
          setCases(result.data)
        }
      }
    } catch (error) {
      console.error('Error loading MDI cases:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadCases()
  }, [patientId, clinic?.id])

  const getStatusBadge = (status: MdiCase['status']) => {
    const statusInfo = mdiService.getCaseStatusInfo(status)
    return (
      <Badge className={`${statusInfo.bgColor} ${statusInfo.color} border-0`}>
        {status === 'completed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
        {status === 'in_review' && <Clock className="w-3 h-3 mr-1 animate-pulse" />}
        {status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
        {statusInfo.label}
      </Badge>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return format(date, 'MMM d, yyyy h:mm a')
  }

  const getRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  }

  const isRefillCase = (mdiCase: MdiCase): boolean => {
    const caseType = ((mdiCase as any).caseType || (mdiCase as any).case_type || '').toString().toLowerCase()
    return caseType === 'refill'
  }

  // Don't show section if MDI is not enabled
  if (!mdiEnabled && !isLoading) {
    return null
  }

  return (
    <Card className="bg-white border border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
            <Stethoscope className="w-4 h-4 text-indigo-500" />
            MDI Telehealth Cases
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadCases}
            disabled={isLoading}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : cases.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Stethoscope className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm text-slate-500">No MDI cases yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Cases will appear here when intakes are sent to MDI
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {cases.map((mdiCase) => (
              <div
                key={mdiCase.id}
                className="p-4 rounded-lg bg-slate-50 border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(mdiCase.status)}
                    <Badge variant="outline" className="text-slate-600 border-slate-200">
                      {mdiCase.program.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Badge>
                    {isRefillCase(mdiCase) && (
                      <Badge variant="outline" className="text-violet-300 border-violet-400/40 bg-violet-500/10">
                        Refill
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">
                    {getRelativeTime(mdiCase.createdAt)}
                  </span>
                </div>

                <div className="space-y-2">
                  {/* Case Details */}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>Source: {mdiCase.source}</span>
                    {mdiCase.mdiCaseId && (
                      <>
                        <span>•</span>
                        <span className="font-mono">MDI #{mdiCase.mdiCaseId.slice(0, 8)}</span>
                      </>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div className="text-xs text-slate-400">
                    Created {formatDate(mdiCase.createdAt)}
                    {mdiCase.completedAt && (
                      <span className="ml-2">• Completed {formatDate(mdiCase.completedAt)}</span>
                    )}
                  </div>

                  {/* Actions for completed cases */}
                  {mdiCase.status === 'completed' && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                      {mdiCase.encounterId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewEncounter?.(mdiCase.encounterId!)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          View Encounter
                          <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                      {mdiCase.prescriptionId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewPrescription?.(mdiCase.prescriptionId!)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Pill className="w-3 h-3 mr-1" />
                          View Prescription
                          <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Status message for in-progress cases */}
                  {mdiCase.status === 'in_review' && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200 text-xs text-indigo-600">
                      <Clock className="w-3 h-3 animate-pulse" />
                      MDI clinician is reviewing this case
                    </div>
                  )}

                  {/* Error message for failed cases */}
                  {mdiCase.status === 'failed' && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200 text-xs text-red-500">
                      <AlertCircle className="w-3 h-3" />
                      Case submission failed. Please try again.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default MdiCasesSection







